import { supabase } from '@/lib/supabase';

export interface ConcertTicket {
  id: string;
  user_id: string;
  concert_id: string;
  artist_name: string;
  concert_title: string;
  venue: string;
  location: string | null;
  concert_date: string;
  tickets: number;
  unit_price: number;
  total_price: number;
  customer_name: string;
  customer_phone: string | null;
  status: string;
  created_at: string;
}

// Convertit un prix affiché ("10 000 FCFA", "Gratuit") en nombre (XOF).
export function parseConcertPrice(price: string): number {
  if (!price || price.toLowerCase() === 'gratuit') return 0;
  const cleaned = price.replace(/[^\d]/g, '');
  return Number(cleaned) || 0;
}

export interface BookTicketInput {
  concertId: string;
  artistName: string;
  concertTitle: string;
  venue: string;
  location: string;
  concertDate: string;
  tickets: number;
  unitPrice: number;
  customerName: string;
  customerPhone: string;
}

export async function bookConcertTicket(input: BookTicketInput): Promise<ConcertTicket> {
  const { data: sessionData } = await supabase.auth.getSession();
  const user = sessionData?.session?.user;
  if (!user) throw new Error('Vous devez être connecté pour réserver.');

  const total = input.unitPrice * input.tickets;

  const { data, error } = await supabase
    .from('concert_tickets')
    .insert({
      user_id: user.id,
      concert_id: input.concertId,
      artist_name: input.artistName,
      concert_title: input.concertTitle,
      venue: input.venue,
      location: input.location || null,
      concert_date: input.concertDate,
      tickets: input.tickets,
      unit_price: input.unitPrice,
      total_price: total,
      customer_name: input.customerName,
      customer_phone: input.customerPhone || null,
    })
    .select()
    .single();
  if (error) throw error;

  const ticket = data as ConcertTicket;

  // Envoi de l'email de confirmation avec le billet
  if (user.email) {
    try {
      await supabase.functions.invoke('send-email', {
        body: {
          to: user.email,
          subject: `Votre billet — ${ticket.artist_name} (${ticket.concert_title})`,
          html: buildTicketEmailHtml(ticket),
        },
      });
    } catch {
      // Silencieux — l'email de confirmation est non critique
    }
  }

  return ticket;
}

function buildTicketEmailHtml(ticket: ConcertTicket): string {
  const date = new Date(`${ticket.concert_date}T00:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const totalLabel =
    ticket.total_price === 0
      ? 'Gratuit'
      : `${ticket.total_price.toLocaleString('fr-FR')} FCFA`;

  return `
    <div style="font-family:Arial,sans-serif;max-width:560px;margin:0 auto;background:#ffffff;border:1px solid #eeeeee;border-radius:12px;overflow:hidden;">
      <div style="background:#111827;padding:24px;text-align:center;">
        <div style="font-size:28px;line-height:1;">🎫</div>
        <h1 style="color:#ffffff;font-size:20px;margin:12px 0 0;">Réservation confirmée</h1>
        <p style="color:#9ca3af;font-size:13px;margin:6px 0 0;">WorldApeiron — Billetterie</p>
      </div>
      <div style="padding:28px;">
        <p style="font-size:14px;color:#374151;margin:0 0 20px;">Bonjour ${ticket.customer_name},</p>
        <p style="font-size:14px;color:#374151;margin:0 0 24px;line-height:1.6;">
          Merci pour votre réservation ! Voici le récapitulatif de votre billet.
        </p>
        <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:20px;margin-bottom:24px;">
          <div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eeeeee;">
            <span style="color:#6b7280;font-size:13px;">Artiste</span>
            <span style="color:#111827;font-size:14px;font-weight:bold;text-align:right;">${ticket.artist_name}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eeeeee;">
            <span style="color:#6b7280;font-size:13px;">Concert</span>
            <span style="color:#111827;font-size:14px;text-align:right;">${ticket.concert_title}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eeeeee;">
            <span style="color:#6b7280;font-size:13px;">Date</span>
            <span style="color:#111827;font-size:14px;text-align:right;text-transform:capitalize;">${date}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eeeeee;">
            <span style="color:#6b7280;font-size:13px;">Lieu</span>
            <span style="color:#111827;font-size:14px;text-align:right;">${ticket.venue}${ticket.location ? `, ${ticket.location}` : ''}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eeeeee;">
            <span style="color:#6b7280;font-size:13px;">Nombre de billets</span>
            <span style="color:#111827;font-size:14px;font-weight:bold;">${ticket.tickets}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:10px 0 0;">
            <span style="color:#6b7280;font-size:13px;">Total</span>
            <span style="color:#111827;font-size:16px;font-weight:bold;">${totalLabel}</span>
          </div>
        </div>
        <p style="font-size:13px;color:#6b7280;margin:0 0 8px;">
          N° de réservation : <strong style="color:#111827;">${ticket.id.slice(0, 8).toUpperCase()}</strong>
        </p>
        <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0;">
          Présentez simplement votre nom et votre numéro de téléphone à l'entrée. À très bientôt !
        </p>
      </div>
      <div style="background:#f9fafb;padding:16px;text-align:center;border-top:1px solid #eeeeee;">
        <p style="font-size:11px;color:#9ca3af;margin:0;">WorldApeiron — Toute l'Afrique connectée</p>
      </div>
    </div>
  `;
}